import { useEffect } from "react";

interface PendingCallJoin {
  channelId: string;
  callType: "audio" | "video";
  channelName: string;
}

interface UsePendingCallAutoJoinArgs {
  pendingCallJoin: PendingCallJoin | null | undefined;
  channelId: string | null;
  handleJoinCall: (
    callType: "audio" | "video",
    channelName: string,
    options: { skipNotification: boolean },
  ) => unknown;
  onPendingCallJoinHandled?: () => void;
}

/**
 * Auto-joins a call when `MessageArea` was navigated here from an
 * incoming-call accept. Fires once per matching `pendingCallJoin`
 * intent, on a 150ms timeout to let the channel mount settle, and
 * clears the parent's pending-join state through
 * `onPendingCallJoinHandled`.
 *
 * The 150ms delay matches the pre-existing behaviour and gives the
 * call dialog stack time to swap from the incoming-call sheet to the
 * in-app call dialog without flashing the channel header.
 */
export function usePendingCallAutoJoin({
  pendingCallJoin,
  channelId,
  handleJoinCall,
  onPendingCallJoinHandled,
}: UsePendingCallAutoJoinArgs) {
  useEffect(() => {
    if (!pendingCallJoin || pendingCallJoin.channelId !== channelId) return;
    const { callType, channelName: callChannelName } = pendingCallJoin;
    const timer = setTimeout(() => {
      void handleJoinCall(callType, callChannelName || "channel", {
        skipNotification: true,
      });
      onPendingCallJoinHandled?.();
    }, 150);
    return () => clearTimeout(timer);
  }, [pendingCallJoin, channelId, handleJoinCall, onPendingCallJoinHandled]);
}
